import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSalesActivitiesStore } from '../../../store/salesActivitiesStore';
import { ActivityKind, ActivityPriority } from '../../../services/salesActivitiesApi';
import { customersApi, Customer } from '../../../services/customersApi';
import styles from './SalesRepLogActivity.module.css';

// 10 kinds total (VP-SALES-006 / migration 044): call/visit/meeting/note
// existed before; the other 6 are new.
const kindOptions: { backend: ActivityKind; label: string }[] = [
  { backend: 'call', label: 'Call' },
  { backend: 'visit', label: 'Visit' },
  { backend: 'meeting', label: 'Meeting' },
  { backend: 'note', label: 'Note' },
  { backend: 'follow_up', label: 'Follow-Up' },
  { backend: 'pricing', label: 'Pricing' },
  { backend: 'collection', label: 'Collection' },
  { backend: 'new_account', label: 'New Account' },
  { backend: 'display', label: 'Display' },
  { backend: 'other', label: 'Other' },
];

const priorityOptions: { backend: ActivityPriority; label: string }[] = [
  { backend: 'low', label: 'Low' },
  { backend: 'medium', label: 'Medium' },
  { backend: 'high', label: 'High' },
];

const SalesRepLogActivity: React.FC = () => {
  const navigate = useNavigate();
  const create = useSalesActivitiesStore((s) => s.create);
  const saving = useSalesActivitiesStore((s) => s.saving);
  const error = useSalesActivitiesStore((s) => s.error);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [kind, setKind] = useState<ActivityKind>('call');
  const [priority, setPriority] = useState<ActivityPriority>('medium');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [completed, setCompleted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    customersApi.list({ limit: 200 })
      .then((r: any) => {
        const data = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        setCustomers(data);
      })
      .catch(() => setCustomers([]));
  }, []);

  const handleSave = async () => {
    setSubmitError(null);
    if (!subject.trim()) {
      setSubmitError('Subject is required');
      return;
    }
    try {
      await create({
        kind,
        subject: subject.trim(),
        notes: notes.trim() || null,
        customer_id: customerId === '' ? null : customerId,
        follow_up_at: followUp ? new Date(followUp).toISOString() : null,
        priority,
        completed_at: completed ? new Date().toISOString() : null,
      });
      navigate('/activities');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save activity');
    }
  };

  const handleCancel = () => navigate('/activities');

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Log Activity</h2>
          <button className={styles.cancel} onClick={handleCancel}>Cancel</button>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Customer</label>
          <select
            className={styles.select}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Select a Customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.store_name || c.full_name || c.email}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Activity Type</label>
          <div className={styles.pillRow}>
            {kindOptions.map((opt) => (
              <button
                key={opt.backend}
                className={`${styles.pill} ${kind === opt.backend ? styles.pillActive : ''}`}
                onClick={() => setKind(opt.backend)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Priority</label>
          <div className={styles.priorityRow}>
            {priorityOptions.map((opt) => (
              <button
                key={opt.backend}
                type="button"
                className={`${styles.pill} ${styles[`priority_${opt.backend}`]} ${priority === opt.backend ? styles.pillActive : ''}`}
                onClick={() => setPriority(opt.backend)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Subject</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Short summary"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Notes</label>
          <textarea
            className={styles.textarea}
            rows={4}
            placeholder="Add notes about this activity..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Follow-up Date (Optional)</label>
          <input
            type="date"
            className={styles.input}
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
          />
        </div>

        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={completed}
            onChange={(e) => setCompleted(e.target.checked)}
          />
          Mark this activity as completed
        </label>

        {(submitError || error) && (
          <p style={{ color: '#DC2626', fontSize: 13, marginTop: 8 }}>{submitError || error}</p>
        )}

        <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Activity'}
        </button>
      </div>
    </div>
  );
};

export const SalesRepLogActivityPage: React.FC = () => {
  return <SalesRepLogActivity />;
};

export { SalesRepLogActivity };
export default SalesRepLogActivity;
